import {
  createResource,
  createSignal,
  For,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
  type JSX,
} from "solid-js";
import { Atlas } from "./composants/Atlas";
import { Entete } from "./composants/Entete";
import { EtatChargement, EtatErreur, EtatVide } from "./composants/Etats";
import { FournisseurSource } from "./donnees/contexte";
import type { SourcePortefeuille } from "./donnees/source";

/**
 * Racine de l'application. La source est injectée : main.tsx passe la source par
 * défaut, les tests une source factice — aucun composant ne la construit lui-même.
 */
export function App(props: { source: SourcePortefeuille }) {
  // eslint-disable-next-line solid/reactivity -- la source est fixée au montage, par conception.
  const source = props.source;
  const [portefeuille, { refetch }] = createResource(() => source.chargerPortefeuille());

  // L'état de lecture est lu dans l'adresse au montage (src/navigation.ts). Le
  // site ne la réécrit que par replaceState, qui ne déclenche pas `hashchange` :
  // cet événement ne vient donc que de l'extérieur (adresse modifiée à la main,
  // lien collé, précédent / suivant). On remonte alors la vue pour qu'elle suive
  // l'adresse ; les données, déjà chargées, ne sont pas redemandées.
  const [generation, setGeneration] = createSignal(0);
  onMount(() => {
    const suivre = () => setGeneration((g) => g + 1);
    window.addEventListener("hashchange", suivre);
    onCleanup(() => window.removeEventListener("hashchange", suivre));
  });

  /** Tant que rien n'est chargé : l'enseigne, et l'état en clair à la place de la vue. */
  const enAttente = (contenu: JSX.Element) => (
    <>
      <Entete />
      <main id="contenu" class="principal" tabIndex={-1}>
        {contenu}
      </main>
    </>
  );

  return (
    <FournisseurSource source={source}>
      <a class="lien-evitement" href="#contenu">
        Aller au contenu
      </a>
      <div class="appli">
        <Switch>
          <Match when={portefeuille.state === "errored"}>
            {enAttente(
              <EtatErreur erreur={portefeuille.error} onReessayer={() => void refetch()} />,
            )}
          </Match>
          <Match when={portefeuille.state !== "ready"}>{enAttente(<EtatChargement />)}</Match>
          <Match when={portefeuille()}>
            {(p) => (
              <Show when={p().projets.length > 0} fallback={enAttente(<EtatVide />)}>
                {/* Une liste d'un seul élément, la génération : quand elle change, la vue est remontée. */}
                <For each={[generation()]}>{() => <Atlas portefeuille={p()} />}</For>
              </Show>
            )}
          </Match>
        </Switch>
      </div>
    </FournisseurSource>
  );
}
